import { useEffect, useState } from 'react';
import { quranData } from '../data/quran';

interface SurahDynamicProps {
  surahId: number;
  currentTime: number;
  isPlaying: boolean;
  isDarkMode: boolean;
}

// Timings for specific Surahs
const surahTimings: Record<number, { id: number; start: number; end: number }[]> = {
  // Al-Fatiha (ID 1) - Mishary Alafasy
  1: [
    { id: 1, start: 6.5, end: 11.5 },   // Bismillah (5s)
    { id: 2, start: 11.5, end: 17.0 },  // Alhamdu (5.5s)
    { id: 3, start: 17.0, end: 19.0 },  // Ar-Rahman (Shortened)
    { id: 4, start: 19.0, end: 24.0 },  // Maliki (Advanced start by 3s, End at 24s)
    { id: 5, start: 24.0, end: 32.0 },  // Iyyaka (Speed up: Ends at 32s instead of 38s)
    { id: 6, start: 32.0, end: 40.0 },  // Ihdina (Shifted earlier: Starts at 32s)
    { id: 7, start: 40.0, end: 65.0 }   // Sirat (Shifted earlier: Starts at 40s)
  ],
  // Al-Ikhlas (ID 112) - Mishary Alafasy
  112: [
    { id: 1, start: 5.5, end: 9 },    // Qul Huwa Allahu Ahad
    { id: 2, start: 9, end: 12 },     // Allahu Samad
    { id: 3, start: 12, end: 16 },    // Lam Yalid
    { id: 4, start: 16, end: 21 }     // Walam Yakun
  ],
  // Al-Falaq (ID 113) - Mishary Alafasy
  113: [
    { id: 1, start: 6, end: 10 },     // Qul A'udhu
    { id: 2, start: 10, end: 13 },    // Min Sharri
    { id: 3, start: 13, end: 17 },    // Wa min sharri ghasiqin
    { id: 4, start: 17, end: 22 },    // Wa min sharri naffathat
    { id: 5, start: 22, end: 27 }     // Wa min sharri hasidin
  ]
};

export function SurahDynamic({ surahId, currentTime, isPlaying, isDarkMode }: SurahDynamicProps) {
  const surah = quranData[surahId as keyof typeof quranData];
  const timings = surahTimings[surahId] || [];
  const [activeVerseId, setActiveVerseId] = useState<number>(1);

  useEffect(() => {
    // If we have timings, find the active verse
    if (timings.length > 0) {
      // Find the verse where currentTime is between start and end
      const active = timings.find(t => currentTime >= t.start && currentTime < t.end);
      
      if (active) {
        setActiveVerseId(active.id);
      } else if (currentTime < timings[0].start) {
        // Before the first verse starts (Bismillah silent period)
        setActiveVerseId(1);
      } else if (currentTime > timings[timings.length - 1].end) {
        // After the last verse ends
        setActiveVerseId(timings[timings.length - 1].id);
      }
    }
  }, [currentTime, timings]);

  // Find the verse data
  const activeVerse = surah?.verses.find(v => v.id === activeVerseId);
  const currentVerseTiming = timings.find(t => t.id === activeVerseId);

  // Calculate progress for the current verse
  const verseProgress = currentVerseTiming 
    ? Math.max(0, Math.min(100, ((currentTime - currentVerseTiming.start) / (currentVerseTiming.end - currentVerseTiming.start)) * 100))
    : 0;

  if (!surah) return null;

  return (
    <div className={`transition-all duration-500 flex flex-col items-center justify-center min-h-[200px] p-4 rounded-xl border ${isDarkMode ? 'bg-emerald-950/30 border-emerald-900/50' : 'bg-emerald-50/50 border-emerald-100'}`}>
        
        {/* Animated Badge */}
        <div className={`mb-4 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${isDarkMode ? 'bg-emerald-900 text-emerald-300' : 'bg-emerald-100 text-emerald-800'}`}>
            Verse {activeVerseId} / {surah?.verses.length}
        </div>

        {/* Arabic Text - Large & Centered */}
        <div className="w-full text-center mb-4 min-h-[80px] flex items-center justify-center">
            <p 
                className={`text-3xl md:text-4xl font-serif leading-relaxed transition-opacity duration-300 ${isDarkMode ? 'text-emerald-100' : 'text-slate-800'}`} 
                dir="rtl" 
                style={{ fontFamily: 'Traditional Arabic, serif' }}
            >
                {activeVerse?.arabic}
            </p>
        </div>

        {/* Transliteration */}
        <p className={`text-center font-medium text-sm mb-2 transition-colors ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
            {activeVerse?.transliteration}
        </p>

        {/* Translation */}
        <p className={`text-center text-sm italic max-w-xs mx-auto transition-colors ${isDarkMode ? 'text-slate-400' : 'text-stone-500'}`}>
            "{activeVerse?.translation}"
        </p>

        {/* Progress Bar for Verse */}
        {isPlaying && currentVerseTiming && (
             <div className="w-full h-1 bg-slate-200 dark:bg-slate-700 rounded-full mt-6 overflow-hidden">
                <div 
                    className="h-full bg-emerald-500 transition-all duration-300 ease-linear"
                    style={{ 
                        width: `${verseProgress}%` 
                    }}
                ></div>
            </div>
        )}
    </div>
  );
}